import {
    ASYNC_STORAGE_KEY_CAMPAIGN_URL,
    ASYNC_STORAGE_KEY_CAMPAIGN_COOKIE,
    ASYNC_STORAGE_KEY_CAMPAIGN_EXPIRES,
} from '@24i/nxg-core-utils/src/constants';
import { Storage } from '@24i/nxg-sdk-quantum';

import { Destructor, SavedCampaign, SavedCampaignWatcher } from './types';
import { detectAllowTracking, isCampaignValid } from './utils';

const watchers: SavedCampaignWatcher[] = [];

export const registerCampaignWatcher = (watcher: SavedCampaignWatcher): Destructor => {
    watchers.push(watcher);
    return () => {
        const position = watchers.indexOf(watcher);
        if (position >= 0) watchers.splice(position, 1);
    };
};

let cachedCampaign: SavedCampaign | undefined;
let cachedCampaignEmpty = false;
let lastProcessedInitialURL: string | null | undefined;

export const resetCache = async (): Promise<void> => {
    cachedCampaign = undefined;
    cachedCampaignEmpty = false;
    lastProcessedInitialURL = undefined;
};

export const wasInitialUrlAlreadyProcessed = (initialURL: string | null | undefined): boolean =>
    initialURL === lastProcessedInitialURL;

export const markInitialUrlAsProcessed = (initialURL: string | null | undefined): void => {
    lastProcessedInitialURL = initialURL;
};

export const notifyWatchers: SavedCampaignWatcher = (campaign) => {
    cachedCampaign = campaign;
    cachedCampaignEmpty = !campaign;
    watchers.forEach((watcher) => watcher(campaign));
};

export const loadCampaign = async (): Promise<SavedCampaign | undefined> => {
    const savedUrl = await Storage.getItem(ASYNC_STORAGE_KEY_CAMPAIGN_URL);
    const savedCookie = await Storage.getItem(ASYNC_STORAGE_KEY_CAMPAIGN_COOKIE);
    const savedExpires = await Storage.getItem(ASYNC_STORAGE_KEY_CAMPAIGN_EXPIRES);
    if (!savedUrl || !savedExpires) {
        return undefined;
    }
    const allowTracking = await detectAllowTracking();
    const loadedCampaign: SavedCampaign = {
        url: savedUrl,
        cookie: savedCookie || '',
        expires: Number(savedExpires),
        allowTracking,
    };
    return loadedCampaign;
};

export const getValidCampaignSync = (): SavedCampaign | undefined => cachedCampaign;

export const loadValidCampaign = async (): Promise<SavedCampaign | undefined> => {
    if (cachedCampaign || cachedCampaignEmpty) return cachedCampaign;
    const campaign = await loadCampaign();

    // prefer cache in case of parallel loadCampaign and saveCampaign
    if (cachedCampaign || cachedCampaignEmpty) return cachedCampaign;

    const campaignEmpty = !isCampaignValid(campaign);
    const validCampaign = campaignEmpty ? undefined : campaign;
    cachedCampaign = validCampaign;
    cachedCampaignEmpty = campaignEmpty;
    return validCampaign;
};

export const saveCampaign = async (campaign: SavedCampaign): Promise<void> => {
    await Storage.multiSet([
        [ASYNC_STORAGE_KEY_CAMPAIGN_URL, campaign.url],
        [ASYNC_STORAGE_KEY_CAMPAIGN_COOKIE, campaign.cookie],
        [ASYNC_STORAGE_KEY_CAMPAIGN_EXPIRES, campaign.expires.toString()],
    ]);
    notifyWatchers(isCampaignValid(campaign) ? campaign : undefined);
};

export const removeCampaign = async (): Promise<void> => {
    await Storage.removeItem(ASYNC_STORAGE_KEY_CAMPAIGN_URL);
    await Storage.removeItem(ASYNC_STORAGE_KEY_CAMPAIGN_COOKIE);
    await Storage.removeItem(ASYNC_STORAGE_KEY_CAMPAIGN_EXPIRES);
    notifyWatchers(undefined);
};
